import React from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Tag, Tooltip, theme } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";

dayjs.extend(relativeTime);

function fmtPrice(price) {
  const n = Number(price);
  return Number.isFinite(n) ? `AED ${n.toLocaleString()}` : "—";
}

// Presentation only — no data fetching. Renders one part's historical
// supplier-quote matches (supplier, price, warranty, recency,
// close-match highlight). `renderAction`, when supplied, is called per
// match to render an action slot after it (Manage Price's "Use this
// price" button); omitted entirely on the read-only New Query panel.
// `onOpenQuery(query_pk)`, when supplied, renders a clickable "#query_id"
// tag per match (used by the price search page to jump to the source
// query's task drawer); omitted entirely by every other caller.
// Renders nothing for an empty `matches` array — callers decide whether
// to show this component at all.
export function PartHistoryList({ matches = [], renderAction, onOpenQuery }) {
  const { token } = theme.useToken();
  if (!matches.length) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {matches.map((match, i) => (
        <div
          key={match.query_id != null ? `${match.query_id}-${match.supplier_id}-${i}` : i}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "6px 10px", borderRadius: 6,
            border: `1px solid ${match.is_close_match ? token.colorSuccessBorder : token.colorBorderSecondary}`,
            background: match.is_close_match ? token.colorSuccessBg : token.colorBgContainer,
          }}
        >
          {match.is_close_match && (
            <Tooltip title="Same year/spec as this vehicle">
              <CheckCircleFilled style={{ color: token.colorSuccess, fontSize: 12, flexShrink: 0 }} />
            </Tooltip>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: token.colorText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {match.supplier_name || "Unknown supplier"}
            </div>
            <div style={{ fontSize: 11, color: token.colorTextSecondary }}>
              {match.quoted_at ? dayjs(match.quoted_at).fromNow() : "unknown date"}
              {match.warranty_days > 0 && <> · {match.warranty_days}d warranty</>}
              {match.vehicle_year && <> · {match.vehicle_year}</>}
              {match.vehicle_country_spec && <> · {match.vehicle_country_spec}</>}
            </div>
          </div>
          <Tag color="blue" style={{ margin: 0, fontWeight: 700, fontSize: 12 }}>
            {fmtPrice(match.quoted_price)}
          </Tag>
          {onOpenQuery && match.query_id != null && (
            <Tag
              style={{ margin: 0, cursor: "pointer", fontSize: 11 }}
              onClick={() => onOpenQuery(match.query_pk)}
            >
              #{match.query_id}
            </Tag>
          )}
          {renderAction?.(match)}
        </div>
      ))}
    </div>
  );
}
